import { localStorage as safeLocalStorage, sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react'
import { filterJobsForNotification, normalizeNotificationCity } from './notificationScope';
import { Tooltip, ClickAwayListener, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router';
import clsx from "clsx";

import {
    Page,
    Container,
    ReverseSVG,
    ClearSVG,
    FeaturedHeader,
    FeaturedInfoButton
} from "./AvailableJobsPage.styles";

import { formatDate, exitReact } from "../../../utils";
import Dropdown from "./components/Dropdown/Dropdown";
import ShiftTypeFilter from "./components/ShiftTypeFilter/ShiftTypeFilter";
import Shifts from "./components/Shifts/Shifts";
import {PageDataProps} from "../../../App";
import { HygienistProps, JobProps, MembershipStatusResponse } from "utils/types";
import Api from "../../../api";
import {usePersistedState} from "../../../hooks";
import {entities} from "../../../reduxStore";
import {
    distanceOptionsCA,
    distanceOptionsUS, filterByDistance, filterByRate,
    FiltersProps,
    rateOptsAst, rateOptsHyg, sorting, sortOptions,
    useAvailableJobs
} from "./AvailableJobs.utils";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import TalentNavbar from "../../../components/TalentNavbar";
import {
    getEffectiveRateTier,
    getAvailableJobsMembershipCta,
    getMembershipBannerCopy,
    getMembershipRestrictionLines,
    isAdminTempingGoodStanding,
    shouldRenderMembershipNextAction,
    shouldShowMembershipBanner,
    shouldShowRestrictedJobsEmptyState,
} from "../../../utils/membership";

import { JobSeekingStatusPopup, AdminAgreementModal } from './components';
import useActionApi from "../../../hooks/useActionApi";
import DefaultButton from "components/Buttons/DefaultButton";
import PhoneVerificationRequired from "components/PhoneVerificationRequired";
import usePhoneVerificationNavigator from "hooks/usePhoneVerificationNavigator";
import MembershipBannerMessage from "components/MembershipBannerMessage";
import { prefetchRateSelectorConfig } from "../../../hooks/useRateSelectorConfig";
import {
    areAvailableJobsFiltersDefault,
    buildAvailableJobsListKey,
    canIncludeAdminShifts,
    clampShiftTypeIdx,
    filterJobsByShiftType,
    getPreviousFiltersAfterClear,
    getPreviousFiltersStorageKey,
    getShiftTypeAnalyticsValue,
    getShiftTypeEmptyMessage,
    normalizeShiftTypeIdx,
    resolveFreshAvailableJobsHygienist,
    shouldResetAvailableJobsFilters,
    ShiftTypeIdx,
} from "./ShiftTypeFilter.utils";

// @ts-ignore
const TempStars = window.TempStars

const LIST_STATE_KEY = 'availableJobs:lastState';

interface ListStateSnapshot {
    listKey: string;
    scrollTop: number;
    lastViewedShiftId?: number | null;
    filters?: FiltersProps;
    date?: string | null;
    isDateFiltered?: boolean;
}

interface AvailableJobsProps {
    hygienist?: HygienistProps,
    pageData?: PageDataProps,
}

interface FiltersResponse {
    activeFilters?: FiltersProps | null;
    previousFilters?: FiltersProps | null;
}

export default function AvailableJobsPage({}: AvailableJobsProps) {
    const [hygienist, setHygienist] = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    });
    const [membershipStatus, setMembershipStatus] = useState<MembershipStatusResponse | null>(null);
    const [membershipLoading, setMembershipLoading] = useState<boolean>(false);
    const [hasFreshUserEligibilityData, setHasFreshUserEligibilityData] = useState<boolean>(false);
    const [freshUserLoading, setFreshUserLoading] = useState<boolean>(true);
    const [freshUserError, setFreshUserError] = useState<boolean>(false);
    const freshAgreedAdminRef = useRef<string | null | undefined>(undefined);

    const history = useHistory();
    usePhoneVerificationNavigator();
    
    const locationState = useMemo(() => 
        history.location.state as { 
            date?: string, 
            withDate?: boolean, 
            notificationCity?: string,
            preserveFilters?: boolean, 
            selectedSort?: number, 
            distanceIdx?: number, 
            rateIdx?: number, 
            favourite?: boolean,
            shiftTypeIdx?: number,
            locationId?: number,
            officeName?: string,
            sourceJobId?: number,
            officeScope?: { locationId: number; officeName?: string; sourceJobId?: number },
            listKey?: string,
            restoreListState?: boolean
        },
        [history.location.state]
    );
    
    const {date, withDate: withDateFromStorage} = locationState || {};
    const isDateFiltered = !!withDateFromStorage;
    const notificationCity = normalizeNotificationCity(locationState?.notificationCity);
    const officeScope = useMemo(() => {
        if (locationState?.officeScope?.locationId) {
            return locationState.officeScope;
        }
        if (locationState?.locationId) {
            return {
                locationId: locationState.locationId,
                officeName: locationState.officeName,
                sourceJobId: locationState.sourceJobId,
            };
        }
        return null;
    }, [locationState]);
    const isOfficeScoped = !!officeScope?.locationId;
    const adminTempingGoodStanding = isAdminTempingGoodStanding(membershipStatus);
    const hasResolvedAdminTempingEligibility = !membershipLoading && membershipStatus?.currentStatusCode != null;
    const adminSessionAgreed = (() => { try { return safeSessionStorage.getItem('ts_admin_agreed') === '1'; } catch { return false; } })();
    const includeAdminShifts = canIncludeAdminShifts({
        type: hygienist?.type,
        country: hygienist?.country,
        agreedAdmin: freshAgreedAdminRef.current,
        sessionAgreed: adminSessionAgreed,
        membershipResolved: hasResolvedAdminTempingEligibility,
        adminTempingGoodStanding,
        freshUserResolved: hasFreshUserEligibilityData,
    });
    const shiftTypeFilterVisible = includeAdminShifts;
    const [availableJobs, hidedJobs, errorGetJos, loadingJobs, refetchAvailableJobs, phoneVerificationRequired] = useAvailableJobs(
        hygienist?.type || 0,
        isDateFiltered ? date : undefined,
        officeScope?.locationId,
        officeScope?.sourceJobId,
        includeAdminShifts,
        hasFreshUserEligibilityData,
    );
    const [filteredJobs, setFilteredJobs] = useState<JobProps[]>([]);
    // Flag to track when filters have been applied (used for analytics timing)
    const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
    // Track favourite overrides locally so UI updates instantly without refetching
    const [favouriteOverrides, setFavouriteOverrides] = useState<Record<number, 0 | 1>>({});
    
    // Scroll restoration state
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [pendingScrollTop, setPendingScrollTop] = useState<number | null>(null);
    const [recentlyViewedShiftId, setRecentlyViewedShiftId] = useState<number | null>(null);
    const [restoredListKey, setRestoredListKey] = useState<string | null>(null);

    const topPinned = useMemo(() => {
        if (!filteredJobs.length) return [] as JobProps[];
        const sortBoosted = (a: JobProps, b: JobProps) => (Number(!!b.isPinned) - Number(!!a.isPinned)) || ((a.displayRank ?? 9999) - (b.displayRank ?? 9999));
        const inRotation = filteredJobs.filter(j => j.isBoostDisplayed).sort(sortBoosted);
        const cap = Math.min(5, inRotation.length);
        if (cap === 0) return [] as JobProps[];
        return inRotation.slice(0, cap);
    }, [filteredJobs]);

    const orderedJobs = useMemo(() => {
        if (!filteredJobs.length) return [] as JobProps[];
        const pinnedIds = new Set(topPinned.map(j => j.id));
        const rest = filteredJobs.filter(j => !pinnedIds.has(j.id));
        return [...topPinned, ...rest];
    }, [filteredJobs, topPinned]);
    
    const [favourite, setFavourite] = useState<boolean>(false);
    const [selectedSort, setSelectedSort] = useState<number>(0);
    const [distanceIdx, setDistance] = useState<number>(0);
    const [rateIdx, setRate] = useState<number>(0);
    const [shiftTypeIdx, setShiftTypeIdx] = useState<ShiftTypeIdx>(0);
    const [previousFilters, setPreviousFilters] = useState<FiltersProps | null>(null);
    const [type, setType] = useState<'rate' | 'distance' | 'sort' | 'filters' | null>(null)
    const [exitReactState, setExitReactState] = useState<boolean>(false);
    // JSS-related state
    const [showJobSeekingPopup, setShowJobSeekingPopup] = useState<boolean>(false);
    const [showAdminAgreement, setShowAdminAgreement] = useState<boolean>(false);
    const [agreeing, setAgreeing] = useState<boolean>(false);
    const [dontShowAdminAgreementAgain, setDontShowAdminAgreementAgain] = useState<boolean>(false);
    const effectiveShiftTypeIdx = clampShiftTypeIdx(shiftTypeIdx, shiftTypeFilterVisible);

    const distanceOpts = useMemo(() => hygienist.country ? distanceOptionsUS : distanceOptionsCA, [hygienist.country]);
    const rateOpts = useMemo(() => hygienist.type ? rateOptsAst : rateOptsHyg, [hygienist.type]);

    const currentFilters = useMemo(() => ({ 
        selectedSort, 
        distanceIdx, 
        rateIdx, 
        favourite,
        shiftTypeIdx: effectiveShiftTypeIdx,
    }), [selectedSort, distanceIdx, rateIdx, favourite, effectiveShiftTypeIdx]);

    const effectiveRateTier = useMemo(
        () => getEffectiveRateTier(membershipStatus),
        [membershipStatus]
    );

    const applyFilters = useCallback((filtersToApply: FiltersProps, jobs: JobProps[]) => {
        const { selectedSort, distanceIdx, rateIdx, favourite, shiftTypeIdx } = filtersToApply;
        const shiftTypeJobs = filterJobsByShiftType(jobs, shiftTypeIdx);
        const sortedJobs = sorting(selectedSort, shiftTypeJobs);
        const filteredByDistance = filterByDistance(distanceIdx, sortedJobs);
        const filteredByRate = filterByRate(effectiveRateTier, rateIdx, filteredByDistance, rateOpts);
        const finalJobs = favourite ? filteredByRate.filter(job => job.isFavouriteDentist) : filteredByRate;
        setFilteredJobs(finalJobs);
        setFiltersApplied(true);
    }, [effectiveRateTier, rateOpts]);

    const [loadingSaveFilters, errorSaveFilters, saveUserFilters] = useActionApi<{activeFilters?: FiltersProps | null, previousFilters?: FiltersProps | null}, void>({
        apiMethod: Api.saveUserFilters,
    });
    
    const [loadingGetFilters, errorGetFilters, getUserFilters] = useActionApi<void, FiltersResponse>({
        apiMethod: Api.getUserFilters,
        onSuccess: useCallback((response: FiltersResponse) => {
            if (response?.previousFilters) {
                setPreviousFilters(response.previousFilters);
            } else if (response?.activeFilters) {
                setPreviousFilters(response.activeFilters);
            }
            if (!shouldResetAvailableJobsFilters(
                locationState?.preserveFilters,
                locationState?.restoreListState,
            )) return;
            const defaultFilters = { selectedSort: 0, distanceIdx: 0, rateIdx: 0, favourite: false, shiftTypeIdx: 0 as ShiftTypeIdx };
            setSelectedSort(defaultFilters.selectedSort);
            setDistance(defaultFilters.distanceIdx);
            setRate(defaultFilters.rateIdx);
            setFavourite(defaultFilters.favourite);
            setShiftTypeIdx(defaultFilters.shiftTypeIdx);
        }, [locationState?.preserveFilters, locationState?.restoreListState, setPreviousFilters, setSelectedSort, setDistance, setRate, setFavourite, setShiftTypeIdx]),
    });

    const previousFiltersKey = getPreviousFiltersStorageKey(hygienist?.id);
    const writePreviousFiltersLocal = useCallback((filters: FiltersProps) => {
        if (!previousFiltersKey) return;
        try { safeLocalStorage.setItem(previousFiltersKey, JSON.stringify(filters)); } catch (_) {}
    }, [previousFiltersKey]);
    const readPreviousFiltersLocal = useCallback((): FiltersProps | null => {
        if (!previousFiltersKey) return null;
        try { const raw = safeLocalStorage.getItem(previousFiltersKey); return raw ? JSON.parse(raw) as FiltersProps : null; } catch (_) { return null; }
    }, [previousFiltersKey]);
    const clearPreviousFiltersLocal = useCallback(() => {
        if (!previousFiltersKey) return;
        try { safeLocalStorage.removeItem(previousFiltersKey); } catch (_) {}
    }, [previousFiltersKey]);

    // Computed flag indicating all initial data and filters are loaded
    const isDataReady = hasFreshUserEligibilityData && !loadingJobs && !loadingGetFilters;

    useEffect(() => {
        (async () => {
            // Prefetch rate selector config (warms cache for rate calculations)
            prefetchRateSelectorConfig().catch(() => {
                // Silent fail - will use defaults
            });
            
            await getUserFilters();
            setPreviousFilters(prev => prev ?? readPreviousFiltersLocal());
            if (shouldResetAvailableJobsFilters(
                locationState?.preserveFilters,
                locationState?.restoreListState,
            )) {
                setSelectedSort(0); setDistance(0); setRate(0); setFavourite(false); setShiftTypeIdx(0);
            }
        })();
    }, [getUserFilters, readPreviousFiltersLocal, locationState?.preserveFilters, locationState?.restoreListState]);

    useEffect(() => {
        let isMounted = true;

        const loadMembership = async () => {
            setMembershipLoading(true);
            try {
                const membership = await Api.getMembershipStatus();
                if (isMounted) {
                    setMembershipStatus(membership);
                }
            } catch {
                if (isMounted) {
                    setMembershipStatus(null);
                }
            } finally {
                if (isMounted) {
                    setMembershipLoading(false);
                }
            }
        };

        loadMembership();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (locationState?.preserveFilters) return;
        if (pendingScrollTop === null) {
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            });
        }
    }, [date, isDateFiltered, locationState?.preserveFilters, pendingScrollTop]);

    // Restore filters from locationState if preserveFilters is true
    useEffect(() => {
        if (!locationState?.preserveFilters) return;
        if (typeof locationState.selectedSort === 'number') setSelectedSort(locationState.selectedSort);
        if (typeof locationState.distanceIdx === 'number') setDistance(locationState.distanceIdx);
        if (typeof locationState.rateIdx === 'number') setRate(locationState.rateIdx);
        if (typeof locationState.favourite === 'boolean') setFavourite(locationState.favourite);
        if (typeof locationState.shiftTypeIdx === 'number') setShiftTypeIdx(normalizeShiftTypeIdx(locationState.shiftTypeIdx));
    }, [locationState]);

    // Restore scroll position and highlight from sessionStorage
    useEffect(() => {
        const raw = safeSessionStorage.getItem(LIST_STATE_KEY);
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw) as ListStateSnapshot;
            const shouldRestore = locationState?.restoreListState ? parsed.listKey === locationState.listKey : false;
            if (!shouldRestore) return;
            if (parsed.filters) {
                setSelectedSort(parsed.filters.selectedSort ?? 0);
                setDistance(parsed.filters.distanceIdx ?? 0);
                setRate(parsed.filters.rateIdx ?? 0);
                setFavourite(parsed.filters.favourite ?? false);
                setShiftTypeIdx(normalizeShiftTypeIdx(parsed.filters.shiftTypeIdx));
            }
            setPendingScrollTop(typeof parsed.scrollTop === 'number' ? parsed.scrollTop : null);
            setRecentlyViewedShiftId(parsed.lastViewedShiftId ?? null);
            setRestoredListKey(parsed.listKey);
        } catch (_) {}
    }, [locationState?.listKey, locationState?.restoreListState]);

    // Apply scroll restoration after data is ready
    useEffect(() => {
        if (pendingScrollTop === null || !isDataReady) return;
        const scrollNode = scrollContainerRef.current || document.scrollingElement || document.documentElement;
        if (!scrollNode) return;
        const applyScroll = () => {
            scrollNode.scrollTo({ top: pendingScrollTop, left: 0, behavior: 'auto' });
        };
        const frame = requestAnimationFrame(() => {
            applyScroll();
            window.setTimeout(applyScroll, 50);
        });
        return () => {
            cancelAnimationFrame(frame);
        };
    }, [isDataReady, pendingScrollTop]);

    useEffect(() => {
        setFiltersApplied(false);
    }, [date, isDateFiltered]);

    // Derive jobs merged with any local favourite overrides (keyed by locationId)
    const jobsWithOverrides = useMemo(() => {
        if (!availableJobs?.length) return [] as JobProps[];
        return filterJobsForNotification(availableJobs, notificationCity).map(job => ({
            ...job,
            isFavouriteDentist: (favouriteOverrides[job.locationId] !== undefined)
                ? favouriteOverrides[job.locationId]
                : job.isFavouriteDentist,
        }));
    }, [availableJobs, favouriteOverrides, notificationCity]);

    const jobsForSelectedShiftType = useMemo(
        () => filterJobsByShiftType(jobsWithOverrides, effectiveShiftTypeIdx),
        [jobsWithOverrides, effectiveShiftTypeIdx]
    );

    const hiddenJobsForSelectedShiftType = useMemo(
        () => filterJobsByShiftType(filterJobsForNotification(hidedJobs, notificationCity), effectiveShiftTypeIdx),
        [hidedJobs, effectiveShiftTypeIdx, notificationCity]
    );

    useEffect(() => {
        if (jobsWithOverrides.length > 0) {
            applyFilters(currentFilters, jobsWithOverrides);
        } else if (isDataReady) {
            // Zero shifts available - still mark filters as applied for analytics
            setFilteredJobs([]);
            setFiltersApplied(true);
        }
    }, [currentFilters, jobsWithOverrides, applyFilters, isDataReady]);

    const readScrollTop = useCallback(() => {
        if (scrollContainerRef.current) {
            return scrollContainerRef.current.scrollTop;
        }
        const scrollingElement = document.scrollingElement || document.documentElement;
        return scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    }, []);

    const goToPage = useCallback((id: number, filter: any, isFavouritedByOffice: boolean) => {
        const currentUser = TempStars.User.getCurrentUser();
        if(!currentUser?.hygienist?.Verified){
            TempStars.Verification.VerificationDescription(
                setExitReactState,
                () => { history.push('hygienistProfile'); },
                () => { history.push('hygienistContactSupport'); }
            )
        } else {
            if (hygienist && !hygienist.Verified) {
                setHygienist({
                    ...hygienist,
                    Verified: currentUser.hygienist.Verified
                });
            }
            
            // Save scroll position and list state before navigating
            const currentListKey = buildAvailableJobsListKey(
                isDateFiltered && date ? date : 'all',
                currentFilters
            );
            const scrollTop = readScrollTop();
            try {
                const snapshot: ListStateSnapshot = {
                    listKey: currentListKey,
                    scrollTop,
                    lastViewedShiftId: id,
                    filters: currentFilters,
                    date: isDateFiltered ? date : null,
                    isDateFiltered,
                };
                safeSessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(snapshot));
            } catch (_) {}

            const positionInList = orderedJobs.findIndex(j => j.id === id) + 1;
            const totalInList = orderedJobs.length;
            
            // Include showJobSeekingStatus in link state for job-details page
            const linkState = {
                jobId: id, 
                filter, 
                isFavouritedByOffice,
                currentFilters,
                showJobSeekingStatus: hygienist?.showForHire,
                isDateFiltered,
                officeScope: officeScope || undefined,
                listKey: currentListKey,
                restoreListState: true,
                positionInList,
                totalInList,
            };
            const hasNonDefaultFilters = !areAvailableJobsFiltersDefault(currentFilters);
            
            if (hasNonDefaultFilters) {
                try { saveUserFilters({ previousFilters: currentFilters, activeFilters: null }); } catch (_) {}
                writePreviousFiltersLocal(currentFilters);
            } else {
                try { saveUserFilters({ activeFilters: null }); } catch (_) {}
            }

            // T2-7022: prepare the existing list entry so both the in-app Back
            // button and the browser Back button can use a real history POP
            // while retaining the same filter/scroll/highlight restoration.
            const currentLocationState = (history.location.state || {}) as Record<string, unknown>;
            history.replace('availableJobs', {
                ...currentLocationState,
                ...currentFilters,
                ...(isDateFiltered && date ? { date, withDate: true } : {}),
                ...(officeScope ? {
                    officeScope,
                    locationId: officeScope.locationId,
                    officeName: officeScope.officeName,
                    sourceJobId: officeScope.sourceJobId,
                } : {}),
                preserveFilters: true,
                restoreListState: true,
                listKey: currentListKey,
            });
            history.push('availableJob', linkState)
        }
    }, [hygienist, setHygienist, history, currentFilters, isDateFiltered, saveUserFilters, writePreviousFiltersLocal, officeScope, date, readScrollTop, orderedJobs]);

    
    useEffect(() => {
        if (exitReactState) {
            history.push('loading')
            exitReact('contact-support')
            setExitReactState(false)
        }
    }, [exitReactState, history])
    
    // JSS: Fetch fresh hygienist data before requesting/rendering shifts. Persisted
    // agreements are honored server-side even without includeAdmin, so a failed
    // eligibility lookup must never leave a mixed response behind a hidden filter.
    const fetchFreshData = useCallback(async () => {
        setFreshUserLoading(true);
        setFreshUserError(false);
        setHasFreshUserEligibilityData(false);
        freshAgreedAdminRef.current = undefined;
        try {
            const freshHygienist = await resolveFreshAvailableJobsHygienist(() => Api.getUser());
            freshAgreedAdminRef.current = freshHygienist.agreedAdmin ?? null;
            setHygienist(freshHygienist);
            setHasFreshUserEligibilityData(true);
        } catch (error) {
            setFreshUserError(true);
        } finally {
            setFreshUserLoading(false);
        }
    }, [setHygienist]);

    useEffect(() => {
        void fetchFreshData();
    }, [fetchFreshData]);

    // Admin Agreement gating — uses freshAgreedAdminRef for accurate post-fetch check
    const adminEligible = hygienist?.type === 1 && hygienist?.country === 0;
    const adminAgreedRaw = hasFreshUserEligibilityData ? freshAgreedAdminRef.current : undefined;
    const adminAgreedYear = adminAgreedRaw ? new Date(adminAgreedRaw).getFullYear() : null;
    const adminDeclinedPermanently = adminAgreedYear !== null && adminAgreedYear <= 1970;
    const adminAgreedPermanently = adminAgreedYear !== null && adminAgreedYear > 1970;
    const adminSessionDismissed = (() => { try { return safeSessionStorage.getItem('ts_admin_dismissed') === '1'; } catch { return false; } })();
    const shouldPromptAdmin = hasFreshUserEligibilityData
        && hasResolvedAdminTempingEligibility
        && adminEligible
        && adminTempingGoodStanding
        && !adminAgreedPermanently
        && !adminDeclinedPermanently
        && !adminSessionAgreed
        && !adminSessionDismissed;
    const shouldShowAdminModal = shouldPromptAdmin && showAdminAgreement;

    useEffect(() => {
        if (!shouldPromptAdmin) return;
        setShowAdminAgreement(true);
    }, [shouldPromptAdmin]);

    // JSS: Show popup if the user is a hygienist/assistant and their status is not set.
    useEffect(() => {
        if (!hasFreshUserEligibilityData) {
            setShowJobSeekingPopup(false);
            return;
        }

        const isHygienistOrAssistant = hygienist && (hygienist.type === 0 || hygienist.type === 1);
        const isStatusNotSet = hygienist?.showForHire == null;

        if (isHygienistOrAssistant && isStatusNotSet) {
            setShowJobSeekingPopup(true);
        } else {
            setShowJobSeekingPopup(false);
        }
    }, [hygienist, hasFreshUserEligibilityData]);

    const handleApplyFilters = useCallback(() => {
        saveUserFilters({ activeFilters: currentFilters });
        setType(null);
    }, [currentFilters, saveUserFilters]);

    // Helper function to check if filters are default values
    const areFiltersDefault = useCallback((filters: FiltersProps) => {
        return areAvailableJobsFiltersDefault(filters);
    }, []);

    // Helper function to check if previous filters are valid (not default)
    const hasValidPreviousFilters = useMemo(() => {
        return previousFilters && !areFiltersDefault(previousFilters);
    }, [previousFilters, areFiltersDefault]);

    const clearAllFilters = useCallback(() => {
        const defaultFilters = { selectedSort: 0, distanceIdx: 0, rateIdx: 0, favourite: false, shiftTypeIdx: 0 as ShiftTypeIdx };
        const restorableFilters = getPreviousFiltersAfterClear<FiltersProps>(currentFilters, previousFilters);
        setSelectedSort(defaultFilters.selectedSort);
        setDistance(defaultFilters.distanceIdx);
        setRate(defaultFilters.rateIdx);
        setFavourite(defaultFilters.favourite);
        setShiftTypeIdx(defaultFilters.shiftTypeIdx);
        if (restorableFilters === currentFilters) {
            saveUserFilters({ activeFilters: null, previousFilters: currentFilters });
            writePreviousFiltersLocal(currentFilters);
        } else {
            saveUserFilters({ activeFilters: null });
        }
        setPreviousFilters(restorableFilters);
        setType(null);
    }, [currentFilters, previousFilters, saveUserFilters, writePreviousFiltersLocal]);

    const restorePreviousFilters = useCallback(() => {
        if (hasValidPreviousFilters && previousFilters) {
            const { selectedSort, distanceIdx, rateIdx, favourite, shiftTypeIdx } = previousFilters;
            setSelectedSort(selectedSort || 0);
            setDistance(distanceIdx || 0);
            setRate(rateIdx || 0);
            setFavourite(favourite || false);
            setShiftTypeIdx(normalizeShiftTypeIdx(shiftTypeIdx));
            saveUserFilters({ activeFilters: previousFilters, previousFilters: null });
            clearPreviousFiltersLocal();
            setPreviousFilters(null);
        }
        setType(null);
    }, [hasValidPreviousFilters, previousFilters, saveUserFilters, clearPreviousFiltersLocal]);

    const unhideShifts = useCallback(async () => {
        if (hidedJobs.length) {
            await TempStars.Api.unhideJobs();
            await refetchAvailableJobs();
        }
    }, [hidedJobs, refetchAvailableJobs]);

    const handleBackNavigation = useCallback(async () => {
        if (isOfficeScoped) {
            (window as any).TempStars?.App?.goReact('hygienistHome');
            return;
        }
        const hasNonDefaultFilters = !areAvailableJobsFiltersDefault(currentFilters);
        
        if (hasNonDefaultFilters) {
            await saveUserFilters({ previousFilters: currentFilters, activeFilters: null });
            writePreviousFiltersLocal(currentFilters);
            setPreviousFilters(currentFilters);
        } else {
            await saveUserFilters({ activeFilters: null });
        }
        exitReact('home');
    }, [currentFilters, saveUserFilters, writePreviousFiltersLocal, isOfficeScoped]);

    // JSS: Handle job seeking status update from popup
    const handleJobSeekingStatusUpdate = (user: any) => {
        if (user?.hygienist) {
            setHygienist(user.hygienist);
            try {
                const userAccount = safeLocalStorage.getItem('userAccount');
                const parsedUserAccount = userAccount ? JSON.parse(userAccount) : {};
                const newAccount = { ...parsedUserAccount, ...user };
                safeLocalStorage.setItem('userAccount', JSON.stringify(newAccount));
            } catch (error) {
                console.error("Failed to update userAccount in local storage", error);
            }
            TempStars.App.loadMenu(user);
        }
        setShowJobSeekingPopup(false);
    };

    const handleAgreeAdmin = async () => {
        setShowAdminAgreement(false);
        try { safeSessionStorage.setItem('ts_admin_agreed', '1'); } catch (_) {}
        try {
            setAgreeing(true);
            if (dontShowAdminAgreementAgain) {
                await Api.updateToken();
                await Api.agreeToAdmin();
                const user = await Api.getUser();
                if (user?.hygienist) {
                    freshAgreedAdminRef.current = user.hygienist.agreedAdmin ?? null;
                    setHasFreshUserEligibilityData(true);
                    setHygienist(user.hygienist);
                    try {
                        const uaRaw = safeLocalStorage.getItem('userAccount');
                        const ua = uaRaw ? JSON.parse(uaRaw) : {};
                        safeLocalStorage.setItem('userAccount', JSON.stringify({ ...ua, ...user }));
                    } catch (_) {}
                }
            }
            // This handler closes over the pre-agreement fetch callback. Pass
            // the newly authorized state explicitly so a base-only request can
            // never race and overwrite the mixed Admin/Dental response.
            await refetchAvailableJobs(true);
        } catch (_) {}
        setAgreeing(false);
    };

    const handleDeclineAdmin = async () => {
        setShowAdminAgreement(false);
        try { safeSessionStorage.removeItem('ts_admin_agreed'); } catch (_) {}
        if (dontShowAdminAgreementAgain) {
            try { safeSessionStorage.setItem('ts_admin_dismissed', '1'); } catch (_) {}
            try {
                await Api.updateToken();
                await (Api as any).declineAdminPermanently();
                const user = await Api.getUser();
                if (user?.hygienist) {
                    freshAgreedAdminRef.current = user.hygienist.agreedAdmin ?? null;
                    setHygienist(user.hygienist);
                }
                await refetchAvailableJobs();
            } catch (_) {}
        } else {
            try { safeSessionStorage.setItem('ts_admin_dismissed', '1'); } catch (_) {}
        }
    };

    const favouriteJobsCount = useMemo(() => {
        return jobsForSelectedShiftType.filter(job => job.isFavouriteDentist)?.length || 0;
    }, [jobsForSelectedShiftType]);

    const formattedDate = useMemo(() => {
        return date ? formatDate(date, 'dddd, MMMM D, YYYY') : '';
    }, [date]);


    const handleSortToggle = useCallback(() => {
        setType(type === 'sort' ? null : 'sort');
    }, [type]);

    const handleDistanceToggle = useCallback(() => {
        setType(type === 'distance' ? null : 'distance');
    }, [type]);

    const handleRateToggle = useCallback(() => {
        setType(type === 'rate' ? null : 'rate');
    }, [type]);

    const handleSetFavouriteTrue = useCallback(() => setFavourite(true), []);
    const handleSetFavouriteFalse = useCallback(() => setFavourite(false), []);

    const handleClearFilter = useCallback(() => {
        if (type === 'sort') setSelectedSort(0);
        if (type === 'distance') setDistance(0);
        if (type === 'rate') setRate(0);
        handleApplyFilters();
    }, [type, handleApplyFilters]);

    const dropdownOptions = useMemo(() => {
        return type === 'sort' ? sortOptions :
               type === 'distance' ? distanceOpts :
               rateOpts;
    }, [type, distanceOpts, rateOpts]);

    const dropdownSelected = useMemo(() => {
        return type === 'sort' ? selectedSort :
               type === 'distance' ? distanceIdx :
               rateIdx;
    }, [type, selectedSort, distanceIdx, rateIdx]);

    const dropdownSetSelected = useMemo(() => {
        return type === 'sort' ? setSelectedSort :
               type === 'distance' ? setDistance :
               setRate;
    }, [type]);

    // Handlers to clear individual filters from the inline "x" icon
    const handleClearSort = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedSort !== 0) {
            setSelectedSort(0);
        }
    }, [selectedSort]);

    const handleClearDistance = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (distanceIdx !== 0) {
            setDistance(0);
        }
    }, [distanceIdx]);

    const handleClearRate = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (rateIdx !== 0) {
            setRate(0);
        }
    }, [rateIdx]);
    // Toggle favourite for a job's location and update local overrides for instant UI response
    const updateFavourite = useCallback(async (job: JobProps) => {
        try {
            const isFav = (favouriteOverrides[job.locationId] !== undefined)
                ? favouriteOverrides[job.locationId] === 1
                : job.isFavouriteDentist === 1;
            if (isFav) {
                await Api.removeFavouriteDentistByLocationId(job.locationId);
            } else {
                await Api.setFavouriteDentist(job.dentistId, job.locationId);
            }
            setFavouriteOverrides(prev => ({
                ...prev,
                [job.locationId]: isFav ? 0 : 1,
            }));
        } catch (e) {
            // silent fail; could optionally surface toast
        }
    }, [favouriteOverrides]);

    const [featuredTooltipOpen, setFeaturedTooltipOpen] = useState(false);

    const handleFeaturedTooltipToggle = useCallback(() => {
        setFeaturedTooltipOpen(prev => !prev);
    }, []);

    const handleFeaturedTooltipClose = useCallback(() => {
        setFeaturedTooltipOpen(false);
    }, []);

    useEffect(() => {
        if (isDataReady) {
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            });
        }
    }, [isDataReady]);

    // Get user info for navbar
    const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
    const firstName = hygienist?.firstName || currentUser?.hygienist?.firstName || '';
    const lastName = hygienist?.lastName || '';
    const navbarPhotoUrl = hygienist?.photoUrl || undefined;
    const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
                        hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

    // Enhanced "view shifts" tracking
    const hasTrackedViewShiftsRef = React.useRef(false);
    useEffect(() => {
        if (isDataReady && !membershipLoading && filtersApplied && !hasTrackedViewShiftsRef.current) {
            hasTrackedViewShiftsRef.current = true;
            
            const visibleShifts = filteredJobs;
            const rates = visibleShifts
                .map(j => j.proposedRate || j.avgRate || j.hourlyRate)
                .filter((r): r is number => typeof r === 'number' && !isNaN(r));
            
            TempStars.Analytics.track('view shifts', {
                shiftStartDate: date ? new Date(date).toISOString() : '',
                totalShiftsAvailable: jobsWithOverrides.filter(j => !j.isHided).length,
                shifts_after_filters: filteredJobs.length,
                favourite_filter_active: favourite,
                sort_option: sortOptions[selectedSort],
                distance_filter: distanceOpts[distanceIdx],
                rate_filter: rateOpts[rateIdx],
                shift_type_filter: getShiftTypeAnalyticsValue(effectiveShiftTypeIdx),
                shift_type_filter_idx: effectiveShiftTypeIdx,
                avg_proposed_rate: rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : null,
                min_proposed_rate: rates.length ? Math.min(...rates) : null,
                max_proposed_rate: rates.length ? Math.max(...rates) : null,
                shifts_with_instabook: visibleShifts.filter(j => j.Instabook).length,
                shifts_from_favourites: visibleShifts.filter(j => j.isFavouriteDentist).length,
                boosted_shifts_count: visibleShifts.filter(j => j.isInRotation || j.isBoostDisplayed).length,
                talent_status: hygienist?.status,
                membership_status_code: membershipStatus?.currentStatusCode ?? null,
                membership_rate_tier: effectiveRateTier,
                membership_temp_shift_access: membershipStatus?.tempShiftAccess ?? null,
                membership_overlay_state: membershipStatus?.overlay?.state ?? null,
                membership_overlay_active: membershipStatus?.overlay?.active ?? false,
                membership_overlay_source: membershipStatus?.overlay?.source ?? null,
                talent_type: hygienist?.type,
                admin_temping_role_region_eligible: adminEligible,
                admin_temping_ffs_good_standing: adminTempingGoodStanding,
                admin_temping_prompt_candidate: shouldPromptAdmin,
                admin_temping_agreed_persisted: adminAgreedPermanently,
                admin_temping_declined_permanently: adminDeclinedPermanently,
            });
        }
        
        return () => {
            hasTrackedViewShiftsRef.current = false;
        };
    }, [isDataReady, membershipLoading, filtersApplied, date, filteredJobs, jobsWithOverrides, favourite, selectedSort, distanceIdx, rateIdx, rateOpts, distanceOpts, effectiveShiftTypeIdx, hygienist?.status, hygienist?.type, membershipStatus, effectiveRateTier, adminEligible, adminTempingGoodStanding, shouldPromptAdmin, adminAgreedPermanently, adminDeclinedPermanently]);

    const membershipBanner = membershipStatus
        ? getMembershipBannerCopy(membershipStatus, { firstName })
        : null;
    const membershipCta = getAvailableJobsMembershipCta(membershipStatus);
    const membershipRestrictionLines = getMembershipRestrictionLines(membershipStatus?.restrictions);
    const shouldRenderNextAction = shouldRenderMembershipNextAction(membershipStatus, membershipBanner);
    // Duplicate-review hold: banner + empty state route to password reset /
    // support instead of My Stats (FFS scores are unrelated to this hold).
    const isDuplicateHold = !!membershipStatus?.overlay?.active
        && membershipStatus?.overlay?.state === 'duplicate_review';
    const hasActiveFilters = selectedSort !== 0 || distanceIdx !== 0 || rateIdx !== 0 || favourite !== false || effectiveShiftTypeIdx !== 0;
    const shiftTypeEmptyMessage = getShiftTypeEmptyMessage(effectiveShiftTypeIdx);
    const hasShiftTypeSpecificEmptyState = effectiveShiftTypeIdx !== 0
        && jobsWithOverrides.length > 0
        && orderedJobs.length === 0;
    const showRestrictedEmptyState = isDataReady
        && orderedJobs.length === 0
        && !hasShiftTypeSpecificEmptyState
        && shouldShowRestrictedJobsEmptyState(membershipStatus);

    return (
        <>
            <TalentNavbar
                firstName={firstName}
                lastName={lastName}
                photoUrl={navbarPhotoUrl}
                isHygOrAsst={isHygOrAsst}
                instabookActivated={hygienist?.InstabookActivated === 1}
                isTestAccount={(hygienist as any)?.isTest === 1}
                showAddShiftButton={false}
                showJobBoardButton={false}
            />
            {freshUserError ? (
            <Container>
                <div role="alert" style={{ padding: '24px 16px', textAlign: 'center' }}>
                    <h2>We couldn't load Available Shifts</h2>
                    <p>Try again to refresh your account and shift eligibility.</p>
                    <DefaultButton
                        variant="contained"
                        onClick={() => { void fetchFreshData(); }}
                    >
                        Try Again
                    </DefaultButton>
                </div>
            </Container>
            ) : isDataReady ? (
            <Container ref={scrollContainerRef}>
                <div className="backButtonRow">
                    <button 
                        className="backButton no-fastclick" 
                        onClick={handleBackNavigation}
                        aria-label="Back to Calendar"
                    >
                        <ArrowBackIcon />
                        <span>Back to Calendar</span>
                    </button>
                </div>
                {isOfficeScoped && officeScope?.officeName && (
                    <div className="subtitle">
                        {`Available shifts for ${officeScope.officeName}`}
                    </div>
                )}
                {notificationCity && <div className="subtitle" data-testid="available-jobs-notification-city">
                    {`Available shifts in ${notificationCity}`}
                </div>}
                {(date && isDateFiltered) ? <div className="subtitle">
                    {`Shifts for: ${formattedDate}`}
                </div> : null}
                {membershipStatus && shouldShowMembershipBanner(membershipStatus) && membershipBanner && (
                    <div
                        style={{
                            backgroundColor: membershipBanner.tone === 'error' ? '#FEF3F3' : membershipBanner.tone === 'warning' ? '#FFF8E1' : '#E8F4FD',
                            border: membershipBanner.tone === 'error' ? '1px solid #F5C2C7' : membershipBanner.tone === 'warning' ? '1px solid #F0DFA0' : '1px solid #B6DAFE',
                            borderRadius: 12,
                            padding: 16,
                            marginBottom: 16,
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{membershipBanner.title}</div>
                        <div style={{ color: '#516173', marginBottom: 12 }}>
                            <MembershipBannerMessage copy={membershipBanner} />
                        </div>
                        {isDuplicateHold ? (
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                <DefaultButton
                                    variant="contained"
                                    onClick={() => (window as any).TempStars?.App?.goReact('ForgotPassword')}
                                >
                                    Reset Password
                                </DefaultButton>
                                <DefaultButton
                                    variant="contained"
                                    onClick={() => (window as any).TempStars?.App?.goReact('hygienistContactSupport')}
                                >
                                    Contact Support
                                </DefaultButton>
                            </div>
                        ) : (
                            <DefaultButton
                                variant="contained"
                                onClick={() => (window as any).TempStars?.App?.goReact(membershipCta.path)}
                            >
                                {membershipCta.label}
                            </DefaultButton>
                        )}
                    </div>
                )}
                <div className="favFilter">
                    <div className="shiftTabs">
                        <div
                            className={clsx('favButton', 'no-fastclick', { underlined: !favourite })}
                            onClick={handleSetFavouriteFalse}
                        >
                            All Shifts ({jobsForSelectedShiftType.length})
                        </div>
                        <div
                            className={clsx('favButton', 'no-fastclick', { underlined: favourite })}
                            onClick={handleSetFavouriteTrue}
                        >
                            Favourite offices ({favouriteJobsCount})
                        </div>
                    </div>
                    <div className="filter-actions">
                        {!!hiddenJobsForSelectedShiftType.length && (
                            <div className={clsx('favButton', 'no-fastclick', 'unhideShifts')} onClick={unhideShifts}>
                                Unhide Shifts ({hiddenJobsForSelectedShiftType.length})
                            </div>
                        )}
                        <button className="no-fastclick action-clear" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                        <button
                            className={`no-fastclick action-previous ${!hasValidPreviousFilters ? 'disabled' : ''}`}
                            onClick={hasValidPreviousFilters ? restorePreviousFilters : undefined}
                            disabled={!hasValidPreviousFilters}
                        >
                            Previous Filters
                        </button>
                    </div>
                </div>

                <div className="filteringBox">
                    {shiftTypeFilterVisible && (
                        <ShiftTypeFilter value={effectiveShiftTypeIdx} onChange={setShiftTypeIdx} />
                    )}
                    {shiftTypeFilterVisible && <span className="filterDivider" aria-hidden="true" />}
                    <div className="filterChips">
                        <div className={clsx('filterButton', 'no-fastclick', { active: selectedSort !== 0 })} onClick={handleSortToggle}>
                            <ReverseSVG width={14} color={'#134074'} />
                            Sort: {sortOptions[selectedSort]}
                            <ClearSVG hidden={selectedSort === 0} onClick={handleClearSort} />
                        </div>
                        <div className={clsx('filterButton', 'no-fastclick', { active: distanceIdx !== 0 })} onClick={handleDistanceToggle}>
                            {distanceOpts[distanceIdx]}
                            <ClearSVG hidden={distanceIdx === 0} onClick={handleClearDistance} />
                        </div>
                        <div className={clsx('filterButton', 'no-fastclick', { active: rateIdx !== 0 })} onClick={handleRateToggle}>
                            {rateOpts[rateIdx]}
                            <ClearSVG hidden={rateIdx === 0} onClick={handleClearRate} />
                        </div>
                    </div>
                </div>
                
                {type && <Dropdown
                    className={'muiFilter'}
                    options={dropdownOptions}
                    selected={dropdownSelected}
                    setSelected={dropdownSetSelected}
                    applyFilters={handleApplyFilters}
                    clearFilter={handleClearFilter}
                    type={type}
                />}

                {topPinned.length > 0 && (
                    <FeaturedHeader>
                        <span>⚡ Featured Shifts</span>
                        <ClickAwayListener onClickAway={handleFeaturedTooltipClose}>
                            <Tooltip
                                title={"These are shifts that dental offices have chosen to spotlight because they're looking to fill them right away."}
                                open={featuredTooltipOpen}
                                disableHoverListener
                                disableFocusListener
                                disableTouchListener
                                placement="bottom-start"
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: 'min(320px, calc(100vw - 32px))',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.4,
                                            p: 1,
                                        }
                                    },
                                    popper: {
                                        modifiers: [
                                            { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
                                            { name: 'flip', options: { fallbackPlacements: ['bottom', 'top', 'right', 'left'] } },
                                            { name: 'offset', options: { offset: [0, 8] } },
                                        ]
                                    }
                                }}
                            >
                                <FeaturedInfoButton
                                    size="small"
                                    onClick={handleFeaturedTooltipToggle}
                                    aria-label="Featured shifts info"
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </FeaturedInfoButton>
                            </Tooltip>
                        </ClickAwayListener>
                    </FeaturedHeader>
                )}

                {showRestrictedEmptyState ? (
                    <div
                        style={{
                            backgroundColor: '#F8FAFC',
                            border: '1px solid #D7E3F4',
                            borderRadius: 12,
                            padding: 20,
                            margin: '8px 4px 16px',
                            textAlign: 'left',
                        }}
                    >
                        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8 }}>
                            {isDuplicateHold
                                ? 'Shifts are paused for the moment'
                                : 'No shifts match your current temp access right now'}
                        </div>
                        <div style={{ color: '#516173', marginBottom: 12 }}>
                            {isDuplicateHold
                                ? 'Your account matches an existing TempStars account, so shifts are hidden while we sort it out — see the notice above or check your email for the fastest way back in.'
                                : 'Your current Fair Fresh Start access and the shifts available right now do not overlap.'}
                        </div>
                        {membershipRestrictionLines.map((line) => (
                            <div key={line} style={{ marginBottom: 4 }}>
                                {line}
                            </div>
                        ))}
                        {shouldRenderNextAction && (
                            <div style={{ marginTop: 12, color: '#516173' }}>
                                <strong>{membershipStatus?.nextAction.title}:</strong> {membershipStatus?.nextAction.description}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                            {isDuplicateHold ? (
                                <DefaultButton
                                    variant="contained"
                                    onClick={() => (window as any).TempStars?.App?.goReact('hygienistContactSupport')}
                                >
                                    Contact Support
                                </DefaultButton>
                            ) : (
                                <DefaultButton
                                    variant="contained"
                                    onClick={() => (window as any).TempStars?.App?.goReact(membershipCta.path)}
                                >
                                    {membershipCta.label}
                                </DefaultButton>
                            )}
                            {hasActiveFilters && (
                                <DefaultButton
                                    variant="contained"
                                    onClick={clearAllFilters}
                                >
                                    Clear Filters
                                </DefaultButton>
                            )}
                        </div>
                    </div>
                ) : phoneVerificationRequired ? (
                    <PhoneVerificationRequired testId="talent-available-shifts-phone-verification-required" />
                ) : (
                    <Shifts
                        goToPage={goToPage}
                        availableJobs={orderedJobs}
                    membershipStatus={membershipStatus}
                        errorGetJos={errorGetJos ?? ''}
                        updating={loadingJobs}
                        recentlyViewedShiftId={restoredListKey ? recentlyViewedShiftId : null}
                        emptyMessage={shiftTypeEmptyMessage}
                    />
                )}
            </Container>
            ) : null}
            
            <JobSeekingStatusPopup 
                key={`job-seeking-popup-${showJobSeekingPopup ? 'open' : 'closed'}`}
                isOpen={showJobSeekingPopup}
                onClose={() => {
                    setShowJobSeekingPopup(false);
                }}
                onUpdate={handleJobSeekingStatusUpdate}
            />
            <AdminAgreementModal
                open={shouldShowAdminModal}
                onAgree={handleAgreeAdmin}
                onDecline={handleDeclineAdmin}
                onClose={handleDeclineAdmin}
                dontShowAgain={dontShowAdminAgreementAgain}
                setDontShowAgain={setDontShowAdminAgreementAgain}
                agreeing={agreeing}
            />
            <FixedLoader loading={freshUserLoading || loadingJobs || loadingGetFilters || loadingSaveFilters || membershipLoading || agreeing} />
            
        </>)
}
